import type { DateValue } from "react-aria-components";

import { EventSeverity, EventStatus, EventType } from "@sitescene/types";
import { type Dispatch, useCallback, useContext, useMemo } from "react";

import type { EventFilterScope } from "@/features/events/types/event-filter-scope";
import type { LocationZoneSensorIdSets } from "@/features/events/utils/filter-valid-ids";

import {
	EventFiltersRegistryContext,
	useScopedEventsDispatch,
	useScopedEventsState,
} from "@/context/event-filters-registry";
import { useEventFilterScope } from "@/features/events/context/event-filter-scope";
import { EventFilterScopeContext } from "@/features/events/context/event-filter-scope-context";
import { areEventFiltersActive } from "@/features/events/utils/are-event-filters-active";
import { DEFAULT_SEVERITY_FILTER } from "@/features/events/utils/severity-filter-utils";
import {
	getDefaultStoredEventFilters,
	writeStoredEventFilters,
} from "@/features/events/utils/stored-events-filters";
import { useLocationZoneSensorIdSets } from "@/hooks/useOrgLocationTree";
export enum EventsActions {
	HydrateFilters = "HYDRATE_FILTERS",
	ResetFilters = "RESET_FILTERS",
	SelectAllEvents = "SELECT_ALL_EVENTS",
	SelectEvent = "SELECT_EVENT",
	ToggleBulkEditing = "TOGGLE_BULK_EDITING",
	UpdateBulkEditing = "UPDATE_BULK_EDITING",
	UpdateDateRangeFilter = "UPDATE_DATE_RANGE_FILTER",
	UpdateEventTypesFilter = "UPDATE_EVENT_TYPES_FILTER",
	UpdateLocationsFilter = "UPDATE_LOCATIONS_FILTER",
	UpdateSearchQueryFilter = "UPDATE_SEARCH_QUERY_FILTER",
	UpdateSensorsFilter = "UPDATE_SENSORS_FILTER",
	UpdateSeverityFilter = "UPDATE_SEVERITY_FILTER",
	UpdateStatusFilter = "UPDATE_STATUS_FILTER",
	UpdateZonesFilter = "UPDATE_ZONES_FILTER",
}

export type EventsActionPayload =
	| {
			dateRange: null | { end: DateValue; start: DateValue };
			type: EventsActions.UpdateDateRangeFilter;
	  }
	| {
			eventId: string;
			isSelected: boolean;
			type: EventsActions.SelectEvent;
	  }
	| {
			eventIds: string[];
			type: EventsActions.SelectAllEvents;
	  }
	| {
			eventTypes: EventType[];
			type: EventsActions.UpdateEventTypesFilter;
	  }
	| {
			filters: Partial<
				Pick<
					EventsState,
					| "dateRangeFilter"
					| "eventTypesFilter"
					| "locationsFilter"
					| "searchQueryFilter"
					| "sensorsFilter"
					| "severityFilter"
					| "statusFilter"
					| "zonesFilter"
				>
			>;
			type: EventsActions.HydrateFilters;
	  }
	| {
			isBulkEditing: boolean;
			type: EventsActions.UpdateBulkEditing;
	  }
	| {
			locations: string[];
			type: EventsActions.UpdateLocationsFilter;
	  }
	| {
			searchQuery: string;
			type: EventsActions.UpdateSearchQueryFilter;
	  }
	| {
			sensors: string[];
			type: EventsActions.UpdateSensorsFilter;
	  }
	| {
			severity: EventSeverity | null;
			type: EventsActions.UpdateSeverityFilter;
	  }
	| {
			status: EventStatus | null;
			type: EventsActions.UpdateStatusFilter;
	  }
	| {
			type: EventsActions.ResetFilters;
	  }
	| {
			type: EventsActions.ToggleBulkEditing;
	  }
	| {
			type: EventsActions.UpdateZonesFilter;
			zones: string[];
	  };

/**
 * Initial state for events reducer.
 * Contains all filter and search state for events.
 */
export type EventsState = {
	dateRangeFilter: null | { end: DateValue; start: DateValue };
	eventTypesFilter: EventType[];
	isBulkEditing: boolean;
	locationsFilter: string[];
	searchQueryFilter: string;
	selectedEventIds: Set<string>;
	sensorsFilter: string[];
	severityFilter: EventSeverity | null;
	statusFilter: EventStatus | null;
	zonesFilter: string[];
};

export const initialEventsState: EventsState = {
	dateRangeFilter: null,
	eventTypesFilter: [],
	isBulkEditing: false,
	locationsFilter: [],
	searchQueryFilter: "",
	selectedEventIds: new Set<string>(),
	sensorsFilter: [],
	severityFilter: DEFAULT_SEVERITY_FILTER,
	statusFilter: null,
	zonesFilter: [],
};

/**
 * Reducer function for managing events state.
 * This follows the reducer pattern for predictable state updates.
 */
export const eventsReducer = (
	state: EventsState,
	action: EventsActionPayload,
): EventsState => {
	switch (action.type) {
		case EventsActions.HydrateFilters:
			return {
				...state,
				...action.filters,
			};
		case EventsActions.ResetFilters:
			return {
				...initialEventsState,
			};
		case EventsActions.SelectAllEvents: {
			const newSelectedIds = new Set<string>(action.eventIds);
			return {
				...state,
				selectedEventIds: newSelectedIds,
			};
		}
		case EventsActions.SelectEvent: {
			const newSelectedIds = new Set(state.selectedEventIds);
			if (action.isSelected) {
				newSelectedIds.add(action.eventId);
			} else {
				newSelectedIds.delete(action.eventId);
			}
			return {
				...state,
				selectedEventIds: newSelectedIds,
			};
		}
		case EventsActions.ToggleBulkEditing:
			return {
				...state,
				isBulkEditing: !state.isBulkEditing,
				// Clear selections when exiting bulk edit mode
				selectedEventIds: !state.isBulkEditing
					? state.selectedEventIds
					: new Set<string>(),
			};
		case EventsActions.UpdateBulkEditing:
			return {
				...state,
				isBulkEditing: action.isBulkEditing,
				// Clear selections when exiting bulk edit mode
				selectedEventIds: action.isBulkEditing
					? state.selectedEventIds
					: new Set<string>(),
			};
		case EventsActions.UpdateDateRangeFilter:
			return {
				...state,
				dateRangeFilter: action.dateRange,
			};
		case EventsActions.UpdateEventTypesFilter:
			return {
				...state,
				eventTypesFilter: action.eventTypes,
			};
		case EventsActions.UpdateLocationsFilter:
			return {
				...state,
				locationsFilter: action.locations,
			};
		case EventsActions.UpdateSearchQueryFilter:
			return {
				...state,
				searchQueryFilter: action.searchQuery,
			};
		case EventsActions.UpdateSensorsFilter:
			return {
				...state,
				sensorsFilter: action.sensors,
			};
		case EventsActions.UpdateSeverityFilter:
			return {
				...state,
				severityFilter: action.severity,
			};
		case EventsActions.UpdateStatusFilter:
			return {
				...state,
				statusFilter: action.status,
			};
		case EventsActions.UpdateZonesFilter:
			return {
				...state,
				zonesFilter: action.zones,
			};
		default: {
			return state;
		}
	}
};

/** Partial filter fields merged by `HydrateFilters` (storage, URL, L/Z/S reconcile). */
export type HydratableEventsFilterFields = Partial<
	Pick<
		EventsState,
		| "dateRangeFilter"
		| "eventTypesFilter"
		| "locationsFilter"
		| "searchQueryFilter"
		| "sensorsFilter"
		| "severityFilter"
		| "statusFilter"
		| "zonesFilter"
	>
>;

/**
 * Action creators - helper functions to create action payloads.
 * These provide a clean API for dispatching actions.
 */
export const eventsActions = {
	hydrateFilters: (
		filters: HydratableEventsFilterFields,
	): EventsActionPayload => ({
		filters,
		type: EventsActions.HydrateFilters,
	}),
	resetFilters: (): EventsActionPayload => ({
		type: EventsActions.ResetFilters,
	}),
	selectAllEvents: (eventIds: string[]): EventsActionPayload => ({
		eventIds,
		type: EventsActions.SelectAllEvents,
	}),
	selectEvent: (eventId: string, isSelected: boolean): EventsActionPayload => ({
		eventId,
		isSelected,
		type: EventsActions.SelectEvent,
	}),
	toggleBulkEditing: (): EventsActionPayload => ({
		type: EventsActions.ToggleBulkEditing,
	}),
	updateBulkEditing: (isBulkEditing: boolean): EventsActionPayload => ({
		isBulkEditing,
		type: EventsActions.UpdateBulkEditing,
	}),
	updateDateRangeFilter: (
		dateRange: null | { end: DateValue; start: DateValue },
	): EventsActionPayload => ({
		dateRange,
		type: EventsActions.UpdateDateRangeFilter,
	}),
	updateEventTypesFilter: (eventTypes: EventType[]): EventsActionPayload => ({
		eventTypes,
		type: EventsActions.UpdateEventTypesFilter,
	}),
	updateLocationsFilter: (locations: string[]): EventsActionPayload => ({
		locations,
		type: EventsActions.UpdateLocationsFilter,
	}),
	updateSearchQueryFilter: (searchQuery: string): EventsActionPayload => ({
		searchQuery,
		type: EventsActions.UpdateSearchQueryFilter,
	}),
	updateSensorsFilter: (sensors: string[]): EventsActionPayload => ({
		sensors,
		type: EventsActions.UpdateSensorsFilter,
	}),
	updateSeverityFilter: (
		severity: EventSeverity | null,
	): EventsActionPayload => ({
		severity,
		type: EventsActions.UpdateSeverityFilter,
	}),
	updateStatusFilter: (status: EventStatus | null): EventsActionPayload => ({
		status,
		type: EventsActions.UpdateStatusFilter,
	}),
	updateZonesFilter: (zones: string[]): EventsActionPayload => ({
		type: EventsActions.UpdateZonesFilter,
		zones,
	}),
};

/** Merges partial filter fields into context (system hydration, not a single user edit). */
export const hydrateEventsFilters = (
	dispatch: Dispatch<EventsActionPayload>,
	filters: HydratableEventsFilterFields,
): void => {
	dispatch(eventsActions.hydrateFilters(filters));
};

/** Updates Alert Priority in context. */
export const updateEventsSeverityFilter = (
	dispatch: Dispatch<EventsActionPayload>,
	severity: EventSeverity | null,
): void => {
	dispatch(eventsActions.updateSeverityFilter(severity));
};

/** Updates alert status in context (All Activity). */
export const updateEventsStatusFilter = (
	dispatch: Dispatch<EventsActionPayload>,
	status: EventStatus | null,
): void => {
	dispatch(eventsActions.updateStatusFilter(status));
};

export type ResetEventsFiltersOptions = {
	orgCode?: string;
	scope?: EventFilterScope;
};

/** Resets filter state to dashboard defaults, including full L/Z/S selection when known. */
export const resetEventsFiltersToDefaults = (
	dispatch: Dispatch<EventsActionPayload>,
	idSets: LocationZoneSensorIdSets,
	options?: ResetEventsFiltersOptions,
): void => {
	const defaults = getDefaultStoredEventFilters(idSets);

	dispatch(eventsActions.resetFilters());
	hydrateEventsFilters(dispatch, {
		locationsFilter: defaults.locationsFilter,
		sensorsFilter: defaults.sensorsFilter,
		zonesFilter: defaults.zonesFilter,
	});

	if (options?.orgCode && options.scope) {
		writeStoredEventFilters(options.orgCode, options.scope, defaults, idSets);
	}
};

/**
 * Hook to access events state for the active page scope.
 * @throws Error if used outside of EventFilterScopeProvider
 */
export const useEventsState = (): EventsState => {
	const scope = useEventFilterScope();
	return useScopedEventsState(scope);
};

/**
 * Resolves filter state for event list queries (paginated fetch, export).
 * Uses scoped state when inside EventFilterScopeProvider; otherwise defaults
 * (e.g. watch detail embeds where filters are scoped by watch id only).
 */
export const useEventsFilterState = (): EventsState => {
	const scope = useContext(EventFilterScopeContext);
	const registry = useContext(EventFiltersRegistryContext);

	if (scope === undefined || registry === undefined) {
		return initialEventsState;
	}

	return registry[scope === "dashboard" ? "dashboard" : "allActivity"].state;
};

/**
 * Hook to access events dispatch for the active page scope.
 * @throws Error if used outside of EventFilterScopeProvider
 */
export const useEventsDispatch = (): Dispatch<EventsActionPayload> => {
	const scope = useEventFilterScope();
	return useScopedEventsDispatch(scope);
};

/**
 * Events context type - provides state and helper functions for managing events filters
 */
export interface EventsContextType {
	dateRangeFilter: null | { end: DateValue; start: DateValue };
	eventTypesFilter: EventType[];
	hasActiveFilters: boolean;
	isBulkEditing: boolean;
	locationsFilter: string[];
	resetFilters: () => void;
	searchQueryFilter: string;
	selectAllEvents: (eventIds: string[]) => void;
	selectedEventIds: Set<string>;
	selectEvent: (eventId: string, isSelected: boolean) => void;
	sensorsFilter: string[];
	setDateRangeFilter: (
		dateRange: null | { end: DateValue; start: DateValue },
	) => void;
	setEventTypesFilter: (eventTypes: EventType[]) => void;
	setLocationsFilter: (locations: string[]) => void;
	setSearchQueryFilter: (searchQuery: string) => void;
	setSensorsFilter: (sensors: string[]) => void;
	setSeverityFilter: (severity: EventSeverity | null) => void;
	setStatusFilter: (status: EventStatus | null) => void;
	setZonesFilter: (zones: string[]) => void;
	severityFilter: EventSeverity | null;
	statusFilter: EventStatus | null;
	toggleBulkEditing: () => void;
	toggleEventTypeFilter: (eventType: EventType) => void;
	toggleLocationFilter: (locationId: string) => void;
	toggleSensorFilter: (sensorId: string) => void;
	toggleZoneFilter: (zoneId: string) => void;
	zonesFilter: string[];
}

/**
 * Convenience hook that combines state and dispatch with helper functions.
 * This maintains backward compatibility and provides a clean API.
 *
 * For more granular control, use useEventsState() and useEventsDispatch() separately.
 */
export const useEvents = (): EventsContextType => {
	const state = useEventsState();
	const dispatch = useEventsDispatch();
	const idSets = useLocationZoneSensorIdSets();
	const {
		dateRangeFilter,
		eventTypesFilter,
		locationsFilter,
		searchQueryFilter,
		sensorsFilter,
		severityFilter,
		statusFilter,
		zonesFilter,
	} = state;

	// Helper functions that dispatch actions
	const setSearchQueryFilter = useCallback(
		(searchQuery: string) => {
			dispatch(eventsActions.updateSearchQueryFilter(searchQuery));
		},
		[dispatch],
	);

	const setEventTypesFilter = useCallback(
		(eventTypes: EventType[]) => {
			dispatch(eventsActions.updateEventTypesFilter(eventTypes));
		},
		[dispatch],
	);

	const toggleEventTypeFilter = useCallback(
		(eventType: EventType) => {
			const newTypes = state.eventTypesFilter.includes(eventType)
				? state.eventTypesFilter.filter((type) => type !== eventType)
				: [...state.eventTypesFilter, eventType];
			dispatch(eventsActions.updateEventTypesFilter(newTypes));
		},
		[state.eventTypesFilter, dispatch],
	);

	const setSeverityFilter = useCallback(
		(severity: EventSeverity | null) => {
			updateEventsSeverityFilter(dispatch, severity);
		},
		[dispatch],
	);

	const setDateRangeFilter = useCallback(
		(dateRange: null | { end: DateValue; start: DateValue }) => {
			dispatch(eventsActions.updateDateRangeFilter(dateRange));
		},
		[dispatch],
	);

	const setLocationsFilter = useCallback(
		(locations: string[]) => {
			dispatch(eventsActions.updateLocationsFilter(locations));
		},
		[dispatch],
	);

	const toggleLocationFilter = useCallback(
		(locationId: string) => {
			const newLocations = state.locationsFilter.includes(locationId)
				? state.locationsFilter.filter((id) => id !== locationId)
				: [...state.locationsFilter, locationId];
			dispatch(eventsActions.updateLocationsFilter(newLocations));
		},
		[state.locationsFilter, dispatch],
	);

	const toggleSensorFilter = useCallback(
		(sensorId: string) => {
			const newSensors = state.sensorsFilter.includes(sensorId)
				? state.sensorsFilter.filter((id) => id !== sensorId)
				: [...state.sensorsFilter, sensorId];
			dispatch(eventsActions.updateSensorsFilter(newSensors));
		},
		[state.sensorsFilter, dispatch],
	);
	const setSensorsFilter = useCallback(
		(sensors: string[]) => {
			dispatch(eventsActions.updateSensorsFilter(sensors));
		},
		[dispatch],
	);

	const setStatusFilter = useCallback(
		(status: EventStatus | null) => {
			updateEventsStatusFilter(dispatch, status);
		},
		[dispatch],
	);

	const setZonesFilter = useCallback(
		(zones: string[]) => {
			dispatch(eventsActions.updateZonesFilter(zones));
		},
		[dispatch],
	);

	const toggleZoneFilter = useCallback(
		(zoneId: string) => {
			const newZones = state.zonesFilter.includes(zoneId)
				? state.zonesFilter.filter((id) => id !== zoneId)
				: [...state.zonesFilter, zoneId];
			dispatch(eventsActions.updateZonesFilter(newZones));
		},
		[state.zonesFilter, dispatch],
	);

	const resetFilters = useCallback(() => {
		resetEventsFiltersToDefaults(dispatch, idSets);
	}, [dispatch, idSets]);

	const toggleBulkEditing = useCallback(() => {
		dispatch(eventsActions.toggleBulkEditing());
	}, [dispatch]);

	const selectEvent = useCallback(
		(eventId: string, isSelected: boolean) => {
			dispatch(eventsActions.selectEvent(eventId, isSelected));
		},
		[dispatch],
	);

	const selectAllEvents = useCallback(
		(eventIds: string[]) => {
			dispatch(eventsActions.selectAllEvents(eventIds));
		},
		[dispatch],
	);

	// Computed values — aligned with useCanResetEventFilters (includes locations + alert status).
	const hasActiveFilters = useMemo(
		() =>
			areEventFiltersActive(
				{
					dateRangeFilter,
					eventTypesFilter,
					locationsFilter,
					searchQueryFilter,
					sensorsFilter,
					severityFilter,
					statusFilter,
					zonesFilter,
				},
				idSets,
				{ includeAlertStatus: true },
			),
		[
			idSets,
			dateRangeFilter,
			eventTypesFilter,
			locationsFilter,
			searchQueryFilter,
			sensorsFilter,
			severityFilter,
			statusFilter,
			zonesFilter,
		],
	);

	return useMemo(
		() => ({
			dateRangeFilter: state.dateRangeFilter,
			eventTypesFilter: state.eventTypesFilter,
			hasActiveFilters,
			isBulkEditing: state.isBulkEditing,
			locationsFilter: state.locationsFilter,
			resetFilters,
			searchQueryFilter: state.searchQueryFilter,
			selectAllEvents,
			selectedEventIds: state.selectedEventIds,
			selectEvent,
			sensorsFilter: state.sensorsFilter,
			setDateRangeFilter,
			setEventTypesFilter,
			setLocationsFilter,
			setSearchQueryFilter,
			setSensorsFilter,
			setSeverityFilter,
			setStatusFilter,
			setZonesFilter,
			severityFilter: state.severityFilter,
			statusFilter: state.statusFilter,
			toggleBulkEditing,
			toggleEventTypeFilter,
			toggleLocationFilter,
			toggleSensorFilter,
			toggleZoneFilter,
			zonesFilter: state.zonesFilter,
		}),
		[
			hasActiveFilters,
			state.searchQueryFilter,
			state.eventTypesFilter,
			state.isBulkEditing,
			state.severityFilter,
			state.statusFilter,
			state.dateRangeFilter,
			state.locationsFilter,
			state.sensorsFilter,
			state.zonesFilter,
			state.selectedEventIds,
			setSearchQueryFilter,
			setEventTypesFilter,
			toggleEventTypeFilter,
			setSeverityFilter,
			setStatusFilter,
			setDateRangeFilter,
			setLocationsFilter,
			toggleLocationFilter,
			toggleSensorFilter,
			setSensorsFilter,
			setZonesFilter,
			toggleZoneFilter,
			resetFilters,
			toggleBulkEditing,
			selectAllEvents,
			selectEvent,
		],
	);
};
